// Will Accept write this milestone? Asked by every surface that shows a
// milestone value beside Accept before it shows one: Focus's ready screen, and
// the readiness card on the issue page and on the chat surfaces (which check
// against the session's project, `ChatPage`).
//
// Apply resolves the card's title against the project's milestone list and,
// when nothing matches, leaves the issue's milestone as it is
// (`apply.milestone_unresolved`). So a value the list does not hold must not
// read as if it lands. This reads the SAME list (the picker's
// `GET /milestones?counts=none`, every state) with the SAME comparison
// (`milestoneTitleMatches`: trimmed, exact) — the two cannot disagree about
// what gets written.
//
// `unknown` while the list loads or when it cannot be read (an error, or the
// 401 leg where `useMilestones` sets neither `data` nor `error`): the screen
// then shows the value as before. Apply's own list call fails the same way,
// so claiming "won't change" there would be a guess.

import {
  milestoneTitleMatches,
  milestoneTitleOf,
} from "@loom/shared/readiness";
import type { MilestoneRoadmapResponse } from "@loom/shared/dashboard";

import { useMilestones } from "../dashboard/useMilestones";

export type MilestoneWrite = "written" | "not_written" | "unknown";

/** The pure decision, for the tests and the hook below. */
export const milestoneWriteOf = (
  list: {
    readonly data: MilestoneRoadmapResponse | null;
    readonly loading: boolean;
    readonly error: string | null;
  },
  value: string,
): MilestoneWrite => {
  if (list.loading || list.error !== null || list.data === null) return "unknown";
  if (milestoneTitleOf(value) === null) return "unknown";
  return list.data.milestones.some(({ milestone }) =>
    milestoneTitleMatches(milestone.title, value),
  )
    ? "written"
    : "not_written";
};

export const useMilestoneWrite = (
  projectId: number,
  value: string,
): MilestoneWrite => {
  const list = useMilestones(projectId, { counts: false });
  return milestoneWriteOf(list, value);
};
